import React, { useCallback, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {Card, CardContent, Typography, Grid, Divider, Box} from '@material-ui/core';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import Row from '../../components/BusinessReportRow';
import BusinessValuation from '../../components/BusinessValuation';
import getBalanceSheet from '../../helpers/getBalanceSheet';
import IconButton from '../../components/widgets/IconButton';
import PrintIcon from '@material-ui/icons/Print';
import { useReactToPrint } from 'react-to-print';

const useStyles = makeStyles((theme) => ({
  root: {
    overflow: 'auto',
    margin: 'auto'
  },
  topBottomBorder: {
    borderTop: '2px solid black',
    borderBottom: '2px solid black',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  },
  tally: {
    width: '100%',
    minWidth: '6rem',
    textAlign: 'right',
    margin: '.5rem',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center'
    }
  },
  header: {
    textAlign: 'center',
    fontWeight: 'bold',
  },
  subheader: {
    fontWeight: 'bolder',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
      padding: '10px 0'
    }
  },
  rowHeading: {
    justifyContent: 'flex-end',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center'
    }
  }
}));

const BalanceSheetReport = (props) => {
  const classes = useStyles();

  const quarter = props.quarter + 1;
  const decHist = props.sim.teams[props.teamIndex].decisionHistory;
  const vitHist = props.sim.teams[props.teamIndex].vitalsHistory;
  const newName = vitHist.filter(vital => vital.businessAs);
  const teamName = `${(newName.length > 0 && newName[newName.length - 1].businessAs) || props.sim.teams[props.teamIndex].teamName}, INC.`;

  const getQtrBalanceSheet = useCallback( (q) => {
    return getBalanceSheet(decHist, vitHist)(q)
  }, [decHist, vitHist]
  );

  let currentQtrValues = getQtrBalanceSheet(quarter);
  let lastQtrValues = getQtrBalanceSheet(quarter - 1);

  const printRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `WBW, ${teamName} Financials - Quarter ${quarter}`,
    pageStyle: "margin: 2rem;"
  });

  return (
    <Card className={classes.root}>
      <Box display='flex' justifyContent='flex-end' margin='0.5rem;'>
        <IconButton
          size="small"
          variant="text"
          color="secondary"
          style={{float: 'right'}}
          onClick={ handlePrint }
          icon={PrintIcon}
        />
      </Box>
      <CardContent ref={printRef}>
        <Grid container alignItems='center' justify='center'>
          <Grid item className={classes.header}>
            <Typography className={classes.header}>{teamName}</Typography>
            <Typography className={classes.header}>FINANCIALS</Typography>
            <Typography className={classes.header}>{`QUARTER ${quarter}`}</Typography>
            <br/><br/>
          </Grid>
        </Grid><br/>
        <BusinessValuation
          stockLast={lastQtrValues && lastQtrValues.stockPrice}
          stockCurrent={currentQtrValues.stockPrice}
          valueLast={lastQtrValues && lastQtrValues.valuation}
          valueCurrent={currentQtrValues.valuation}
          netFlowLast={lastQtrValues && lastQtrValues.netCashFlow}
          netFlowCurrent={currentQtrValues.netCashFlow}
        />
        
        <Typography style={{textDecoration: 'underline', textAlign: 'center', fontWeight: 'bold', marginTop: '3rem'}}>Income Statement</Typography><br/>
        <Grid container className={classes.rowHeading} direction='row' alignItems='center'>
          <Grid className={classes.header} item style={{display: 'flex'}}>
            {quarter > 1 && <Typography style={{textAlign: 'center'}} className={classnames(classes.tally, classes.topBottomBorder)}>{`Q${quarter - 1}`}</Typography>}
            <Typography style={{textAlign: 'center'}} className={classnames(classes.tally, classes.topBottomBorder)}>{`Q${quarter}`}</Typography>
          </Grid>
        </Grid>
        <Typography className={classes.subheader}>Income</Typography>
        <Divider/>
        <Row title='Revenue' last={lastQtrValues && lastQtrValues.revenue} current={currentQtrValues.revenue}/>
        <Divider/>
        <Typography className={classes.subheader} style={{marginTop: '1rem'}}>Cost of Goods Sold</Typography>
        <Divider/>
        <Row title='Production Costs' last={lastQtrValues && lastQtrValues.productionCost} current={currentQtrValues.productionCost}/>
        <Row title='Quality Management' last={lastQtrValues && lastQtrValues.qualityManagement} current={currentQtrValues.qualityManagement}/>
        <Divider/>
        <Row title='Total Cost of Goods Sold' last={lastQtrValues && (lastQtrValues.cogs)} current={currentQtrValues.cogs}/>
        <Divider/>
        <Row title='Gross Profit' last={lastQtrValues && (lastQtrValues.revenue - lastQtrValues.cogs)} current={currentQtrValues.revenue  - currentQtrValues.cogs}/>
        <Divider/><br/>
        <Typography className={classes.subheader}>Expenses</Typography>
        <Divider/>
        <Row title='Personnel' last={lastQtrValues && lastQtrValues.personnel} current={currentQtrValues.personnel}/>
        <Row title='Advertising' last={lastQtrValues && lastQtrValues.advertising} current={currentQtrValues.advertising}/>
        <Row title='Market Research' last={lastQtrValues && lastQtrValues.marketResearch} current={currentQtrValues.marketResearch}/>
        <Row title='Product Development' last={lastQtrValues && lastQtrValues.prodDev} current={currentQtrValues.prodDev}/>
        <Row title='Dividends' last={lastQtrValues && lastQtrValues.dividends} current={currentQtrValues.dividends}/>
        <Row title='Loan Payment' last={lastQtrValues && lastQtrValues.loanPayment} current={currentQtrValues.loanPayment}/>
        <Row title='Miscellaneous Expenses' last={lastQtrValues && lastQtrValues.otherExpenses} current={currentQtrValues.otherExpenses}/><br/>
        <Divider/>
        <Row title='Total Expenses' last={lastQtrValues && (lastQtrValues.pandlExpenses) } current={currentQtrValues.pandlExpenses}/>
        <Divider/><br/>
        <Row title='Net Income' last={lastQtrValues && (lastQtrValues.netCashFlow)} current={currentQtrValues.netCashFlow}/>
        <Divider/>

        <Typography style={{textDecoration: 'underline', textAlign: 'center', fontWeight: 'bold', marginTop: '3rem'}}>Balance Sheet</Typography><br/>
        <Typography style={{fontWeight: 'bold', marginTop: '1rem'}}>ASSETS</Typography><br/>
        <Grid container className={classes.rowHeading} direction='row' alignItems='center'>
          <Grid className={classes.header} item style={{display: 'flex'}}>
            {quarter > 1 && <Typography style={{textAlign: 'center'}} className={classnames(classes.tally, classes.topBottomBorder)}>{`Q${quarter - 1}`}</Typography>}
            <Typography style={{textAlign: 'center'}} className={classnames(classes.tally, classes.topBottomBorder)}>{`Q${quarter}`}</Typography>
          </Grid>
        </Grid>
        <Typography className={classes.subheader}>Assets</Typography>
        <Divider/>
        <Row title='Cash' last={lastQtrValues && lastQtrValues.cash} current={currentQtrValues.cash}/>
        <Row title='Inventory' last={lastQtrValues && lastQtrValues.inventory} current={currentQtrValues.inventory}/>
        <Row title='Property' last={lastQtrValues && lastQtrValues.property} current={currentQtrValues.property}/><br/>
        <Divider/>
        <Row title='Total Assets' last={lastQtrValues && lastQtrValues.totAssets} current={currentQtrValues.totAssets}/>
        <Divider/><br/><br/>
        <Typography style={{fontWeight: 'bold'}}>LIABILITIES AND STOCKHOLDERS EQUITY </Typography><br/>
        <Typography className={classes.subheader}>Liabilities</Typography>
        <Divider/>
        <Row title='Long Term Debt' last={lastQtrValues && lastQtrValues.loanLiability} current={currentQtrValues.loanLiability}/>
        <Divider/><br/>

        <Row title='Total Liabilites' last={lastQtrValues && lastQtrValues.totLiabilities} current={currentQtrValues.totLiabilities}/>
        <Divider/><br/>

        <Typography className={classes.subheader}>Stockholder's equity</Typography>
        <Divider/>
        <Row 
          title="Stockholder's equity" 
          last={lastQtrValues && (lastQtrValues.totAssets - lastQtrValues.totLiabilities)} 
          current={currentQtrValues.totAssets - currentQtrValues.totLiabilities}
        />
        <Divider/><br/><br/>
        


        
        
      </CardContent>
    </Card>
  );
};

BalanceSheetReport.propTypes = {
  sim: PropTypes.object.isRequired,
  user: PropTypes.object,
  teamIndex: PropTypes.number.isRequired,
  quarter: PropTypes.number.isRequired
}

export default BalanceSheetReport;
